import { UserStar, Trash2 } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deleteContact } from "@/script/contact";
import { toast } from "@/components/toast/CustomToast";
import type { ContactItem } from "@/hooks/useContactList";

export default function Customer_historyTable_tr({
  opend,
  qnaNo,
  data,
  clickEvent,
  onDeleted,
}: {
  opend: boolean;
  qnaNo: number;
  data: ContactItem;
  clickEvent: () => void;
  onDeleted?: () => void;
}) {
  const queryClient = useQueryClient();
  const { mutate: mutateDelete, isPending: isDeleting } = useMutation({
    mutationFn: (no: number) => deleteContact(no),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === "contact_list",
      });
      toast.success("문의가 삭제되었습니다.");
      onDeleted?.();
    },
    onError: (err: Error) => {
      toast.error(err.message || "문의 삭제에 실패했습니다.");
    },
  });

  function handleDeleteClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (!window.confirm("이 문의를 삭제하시겠습니까?")) return;
    mutateDelete(data.No);
  }

  function formattingDate(fullDate: string) {
    /* data.writeDate 기본값 yyyy-mm-dd hh:mm 에서 시간 제거 */
    return fullDate.split(" ")[0];
  }

  // 답변 여부
  const isAnswered = data.jobState === "완료" || !!data.replycontent;
  // 삭제 가능 여부 (답변이 없을 때만 삭제 가능)
  const canDelete = !isAnswered;

  return (
    <div
      className={`cs_history_grid  ${opend ? "cs_history_grid--opend" : ""} ${isAnswered ? "cs_history_grid--answered" : ""}`}
      onClick={clickEvent}
    >
      <div className="cs_historyTable_cell ta-c">{qnaNo}</div>
      <div className="cs_historyTable_cell cursor-pointer">
        <span>{data.title}</span>
      </div>
      <div className="cs_historyTable_cell ta-c">
        {formattingDate(data.writeDate)}
      </div>
      <div className="cs_historyTable_cell ta-c">
        <span className="text_markAnswered">
          {isAnswered ? "답변 완료" : "답변 대기중"}
        </span>
      </div>
      <div className="cs_historyTable_cell ta-c">
        <span className="cs_historyTable_chevron"></span>
      </div>

      <div
        className="cs_historyTable_contents"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cs_historyTable_question cs_historyTable_qna">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "12px",
            }}
          >
            <div style={{ fontWeight: "600", fontSize: "15px" }}>문의 내용</div>
            <div
              style={{
                width: "28px",
                height: "28px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {canDelete && (
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={handleDeleteClick}
                  style={{
                    padding: "6px",
                    color: "#9ca3af",
                    cursor: isDeleting ? "not-allowed" : "pointer",
                    border: "none",
                    background: "transparent",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    if (!isDeleting) {
                      e.currentTarget.style.color = "#ef4444";
                      e.currentTarget.style.backgroundColor = "#fef2f2";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isDeleting) {
                      e.currentTarget.style.color = "#9ca3af";
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                  aria-label="삭제"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          </div>
          <pre
            style={{ fontFamily: "inherit" }}
            dangerouslySetInnerHTML={{ __html: data.context }}
          ></pre>

          {!!data.filename && (
            <div className="cs_historyTable_fileList">
              <div className="cs_fileLink">{data.filename}</div>
            </div>
          )}
        </div>

        {(isAnswered || data.replycontent || data.Manager || data.Comment) && (
          <div className="cs_historyTable_answer cs_historyTable_qna mt-5">
            <div className="cs_historyTable_asnwer_header">
              {data.Manager && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "12px",
                  }}
                >
                  <UserStar className="text-sig" size={23} />
                  <span className="text-base text-sig font-semibold">
                    메디통
                  </span>
                </div>
              )}
            </div>

            {data.replycontent && (
              <div
                style={{
                  marginBottom: data.Comment ? "12px" : "0",
                }}
                dangerouslySetInnerHTML={{ __html: data.replycontent }}
              ></div>
            )}

            {data.Comment && (
              <div
                style={{
                  padding: "12px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "4px",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    color: "#666",
                    marginBottom: "8px",
                    fontWeight: "500",
                  }}
                ></div>
                <div className="text-base">{data.Comment}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
